import { AlertColor } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import strings from '../../../common/Translation/Translate';
import Notification from '../../../components/Notification';
import { FormDataModel } from '../../../components/Form';
import axiosInstance from '../../../utils/axios';
import { PageProps } from '../../../utils/constants/data';
import { privileges } from '../../../utils/constants/privileges';
import { URLS } from '../../../utils/constants/urls';
import { languages } from '../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import { getListData } from '../../../utils/validators/HelperFunctions';
import ListPage from '../CommonPage';
import MasterDataEntityDrawer from '../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../CommonPage/lastUpdatedColumn';
import { SystemMessageForm } from './AddEditSystemMessage/SystemMessageForm';

const SystemMessageList: FC = () => {
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const languageList = useSelector(languages) as { text: string; value: string }[];

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formModel = useMemo(() => {
    return (languageList || []).map((language, indexLang) => {
      const field = { ...SystemMessageForm(strings)[0] };
      field.field = `editor_${language.value}`;
      field.autoFocus = indexLang === 0;
      field.label = `${field.label} (${language.text})`;
      field.responsive = { xs: 12 };
      return field;
    });
  }, [languageList]);

  const getList = useCallback(
    (filterData: PageProps) => {
      const body = filterData
        ? filterData
        : { page: index + 1, size: pageSize, filter: filter ?? [], sort: ['createdAt:desc'] };
      getListData(setLoading, body, URLS.systemMessages, setPageResponse);
    },
    [index, pageSize, filter],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const openDrawer = useCallback(
    async (recordId: number) => {
      setEditingId(recordId);
      try {
        setDrawerLoading(true);
        const { data } = await axiosInstance.get(URLS.get_edit_systemMessagesId(recordId));
        const container: FormDataModel = {};
        (languageList || []).forEach(language => {
          container[`editor_${language.value}`] = data?.localizedAttributes?.[language.value]?.text ?? '';
        });
        setFormValues(container);
      } catch (error: any) {
        handleCatch(error);
        return;
      } finally {
        setDrawerLoading(false);
      }
      setDrawerOpen(true);
    },
    [languageList],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
  };

  const handleSave = async (body: FormDataModel) => {
    if (!editingId) return;
    try {
      setSaving(true);
      const localizedAttributes = (languageList || []).map(item => ({
        language: item.value,
        text: body[`editor_${item.value}`],
      }));
      await axiosInstance.put(URLS.get_edit_systemMessagesId(editingId), { localizedAttributes });
      setMessage({ display: true, severity: 'success', message: strings.common_update_message });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: strings.systemMessage_title_list,
      subtitle: strings.systemMessage_list_subtitle,
      searchField: 'code',
      searchPlaceholder: 'Search system messages',
      AddButtonText: '',
      listApi: URLS.systemMessages,
      deleteApi: null,
      editPrivilege: privileges.edit_system_message,
      addPrivilege: null,
      customEdit: (id: number) => openDrawer(id),
      tableColumn: [
        { field: 'code', headerName: strings.systemMessage_column_type, flex: 5, sortable: true },
        lastUpdatedTableColumn(),
      ],
    }),
    [openDrawer],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage
        isLoading={loading}
        isWhiteSpace={false}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getList}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={strings.systemMessage_title_list}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={false}
        onClose={closeDrawer}
        onSave={handleSave}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};
export default SystemMessageList;
